import { useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { getAvatars } from '@/helpers/avatarHelpers';
import { getInitials } from '@/pages/Profile/ProfileUtils';
import { UserModel } from '@/apiTypes/UserTypes';
import { NavMenuItem, NavMenuItemConfig } from './NavMenuItem';
import { MoodSection } from './MoodSection';

/** Light-mode active fills mirror each section's hub icon color. See `NavMenuItem`. */
const NAV_ITEMS: NavMenuItemConfig[] = [
    { title: 'Dashboard', iconName: 'home', path: 'dashboard', activeClassName: 'light:bg-gz-marine-300' },
    { title: 'Journeys', iconName: 'map', path: 'journeys', activeClassName: 'light:bg-gz-blue-200' },
    { title: 'Assessments', iconName: 'cognition_2', path: 'assessments', activeClassName: 'light:bg-gz-gold-200' },
    { title: 'Skills', iconName: 'person_play', path: 'skills', activeClassName: 'light:bg-gz-sage-200' },
    { title: 'Profile', iconName: 'account_circle', path: 'profile', activeClassName: 'light:bg-gz-amethyst-200' },
];

export interface NavMenuPanelProps {
    user: UserModel;
    onNavigate: () => void;
    onLogout: () => void;
}

export const NavMenuPanel = ({ user, onNavigate, onLogout }: NavMenuPanelProps) => {
    const { firstName, middleName, lastName, avatarId } = user.userProfile ?? {};

    const avatars = useMemo(() => getAvatars(), []);
    const currentAvatar = avatars.find(a => a.id === avatarId);
    const initials = useMemo(
        () => getInitials(firstName, middleName, lastName),
        [firstName, middleName, lastName],
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <Avatar className="size-[68px] shrink-0 border-2 light:border-gz-marine-700 dark:border-gz-marine-500 lg:size-[77px]">
                    <AvatarImage src={currentAvatar?.src} alt="" />
                    <AvatarFallback className="light:bg-gz-marine-50 dark:bg-gz-marine-500 !text-gz-marine-700 dark:!text-gz-neutral-800 !border-0 type-headline-small">
                        {initials}
                    </AvatarFallback>
                </Avatar>
                {firstName && (
                    <span
                        className="rr-block type-headline-xsmall !font-normal min-w-0 break-words light:text-gz-neutral-900 dark:text-gz-neutral-100"
                        data-heap-redact-text="true"
                    >
                        {firstName}
                    </span>
                )}
            </div>
            <nav aria-label="Main" className="flex flex-col gap-2">
                {NAV_ITEMS.map(item => (
                    <NavMenuItem key={item.path} {...item} onNavigate={onNavigate} />
                ))}
            </nav>
            <div className="flex flex-col gap-3">
                <MoodSection />
                <Button
                    onClick={onLogout}
                    variant="outline"
                    size="default"
                    className="w-[123px] shrink-0 rounded-full py-2 text-[16px] leading-6 lg:w-[136px]"
                    data-track-click="Sign Out Button Clicked"
                    data-track-button-name="sign-out"
                >
                    Sign out
                </Button>
            </div>
        </div>
    );
};
